import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import type { UserChip } from "#app/lib/data";
import { useWorkspaceData, type WorkspaceUser } from "#app/lib/data/workspace-data-provider";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import type { Omittable } from "@salestrak/ui";

export type MostActiveUserRow = {
  userId: string;
  user: UserChip | WorkspaceUser;
  count: number;
};

const fallbackUser = (userId: string): UserChip => ({
  __typename: "User",
  id: userId,
  userId,
  name: "Unknown",
  email: null,
  username: null,
  profileImage: null,
  memberStatus: "active",
});

/**
 * Exact per-actor activity counts from the server aggregation (the `"actor"`
 * grouping token excludes system/null actors).
 */
// The result relaxes: `error` is Apollo's own `error` field, which is
// `undefined` while the query is healthy, and every widget body reads it as a
// truthiness check.
export function useMostActiveUsers(limit: number): Omittable<{
  rows: MostActiveUserRow[];
  maxCount: number;
  loading: boolean;
  error?: ErrorLike;
}> {
  const { userMap } = useWorkspaceData();
  const { where } = useScopedActivityWhere(undefined, {});
  const { counts, ready, error } = useActivityGroupCounts({ groupBy: "actor", where });

  const rows = useMemo(
    () =>
      Object.entries(counts)
        .map(([userId, count]) => ({
          userId,
          user: userMap.get(userId) ?? fallbackUser(userId),
          count,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit),
    [counts, limit, userMap],
  );

  const maxCount = rows[0]?.count ?? 0;

  return { rows, maxCount, loading: !ready, error };
}
